import React, { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet, Image, Link } from "@react-pdf/renderer";
import { DomUtils, parseDocument } from "htmlparser2";
import axios from "axios";


export const renderNoteAsPDF = (html) => {
  const doc = parseDocument(html);
  const nodes = DomUtils.getChildren(doc);

  const renderNodes = (elements, depth = 0) => {
    return elements.map((node, idx) => {
      if (node.type === 'text') {
        return node.data;
      }

      if (node.name === 'br') {
        return <Text key={`br-${depth}-${idx}`}>{'\n'}</Text>;
      }

      if (node.name === 'p') {
        return (
          <Text key={`p-${depth}-${idx}`} style={styles.paragraph}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }
      if (node.name === 'a') {
        const href = node.attribs?.href || "#";
        const linkText = DomUtils.getText(node); // 🔥 Plain text from inside <a>
        return (
          <Link
            key={`a-${depth}-${idx}`}
            src={href.startsWith("http") ? href : `https://${href}`} // ensure it's clickable
            style={{ color: 'blue', textDecoration: 'none' }} // removes underline
          >
            {linkText}
          </Link>
        );
      }


      if (node.name === 'u') {
        return (
          <Text key={`u-${depth}-${idx}`} style={{ textDecoration: 'underline' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }



      if (node.name === 'strong' || node.name === 'b') {
        return (
          <Text key={`b-${depth}-${idx}`} style={{ fontWeight: 'bold' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }

      if (node.name === 'em' || node.name === 'i') {
        return (
          <Text key={`i-${depth}-${idx}`} style={{ fontStyle: 'italic' }}>
            {renderNodes(DomUtils.getChildren(node), depth + 1)}
          </Text>
        );
      }

      if (node.name === 'ul') {
        return DomUtils.getChildren(node)
          .filter(n => n.name === 'li')
          .map((li, i) => (
            <Text key={`ul-${depth}-${idx}-${i}`} style={styles.listItem}>
              • {renderNodes(DomUtils.getChildren(li), depth + 1)}
            </Text>
          ));
      }

      if (node.name === 'ol') {
        return DomUtils.getChildren(node)
          .filter(n => n.name === 'li')
          .map((li, i) => (
            <Text key={`ol-${depth}-${idx}-${i}`} style={styles.listItem}>
              {`${i + 1}. ${renderNodes(DomUtils.getChildren(li), depth + 1)}`}
            </Text>
          ));
      }

      // fallback for unknown tags
      return renderNodes(DomUtils.getChildren(node), depth + 1);
    });
  };

  return renderNodes(nodes);
};

const styles = StyleSheet.create({
  receiptCenter: { textAlign: "center", fontSize: "18px", marginBottom: "5px", fontWeight: 600 },
  page: { padding: 20, backgroundColor: "#fff", fontSize: 12, fontFamily: "Helvetica" },
  headerBox: {
    backgroundColor: "#d1dffa",
    padding: 10,
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  headerTop: { fontSize: 18, color: "#444", marginTop: "5px", fontWeight: "bold" },
  logoSection: { flex: 1 },
  headerP: { fontSize: 12, color: "#444", marginTop: "5px" },
  logoWrap: { flexDirection: "row", alignItems: "center" },
  logo1: { width: 100, height: 40, marginRight: 8 },
  addressText: { fontSize: 11, marginTop: 6 },
  headerRight: {
    textAlign: "right",
    fontSize: 12,
    color: "#333",
    lineHeight: 1.6,
    flex: 1
  },
  section: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  bold: { fontSize: 12, fontWeight: "bold" },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#d1dffa",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#eee",
    paddingVertical: 5,
  },
  cell: { flex: 1, textAlign: "center" },
  cellWide: { flex: 2, textAlign: "center" },
  summaryBox: {
    borderWidth: 1,
    borderColor: "#ccc",
    marginTop: 10,
    borderRadius: 6,
    padding: 10,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 2,
    fontSize: 12,
  },
  grandTotal: {
    backgroundColor: "#003366",
    color: "white",
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "right",
    padding: 10,
    marginTop: 10,
    borderRadius: 4,
  },
  noteSection: { marginTop: 10 },
  noteLabel: { fontSize: 12, fontWeight: "bold", marginBottom: 4 },
  noteText: { fontSize: 12 },
});

const ReceiptDocument = ({ receipt, profile, invoice, logoBase64 }) => {
  const logoImg = `${process.env.REACT_APP_LINK}/${profile.orignal_logoname}` || "";
  const address = profile?.office_address || "";
  const phone = profile?.mobile || "";
  const email = profile?.email || "";
  const taxNo = profile?.taxNo
  const [imagedata, setImageData] = useState()
  const [imagebase64, setImageBase64] = useState()

  console.log(logoImg, address, phone, email)
  // Parse invoice items
  let items = [];
  try {
    if (Array.isArray(invoice?.items)) {
      items = invoice.items;
    } else if (typeof invoice?.items === "string") {
      let parsed = JSON.parse(invoice.items);
      if (typeof parsed === "string") parsed = JSON.parse(parsed); // handle double stringification
      if (Array.isArray(parsed)) items = parsed;
    }
  } catch (e) {
    console.warn("❌ Failed to parse invoice items", e);
    items = [];
  }

  console.log(items);
  console.log("Items for PDF:", items);
  items.forEach((it, i) => {
    console.log(`Item ${i}:`, it, "type of item:", typeof it.item);
  });


  const getImageAsBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob); // returns base64 string
    });
  };



  useEffect(() => {
    getImageAsBase64("https://ecommerce.100billionoms.com/image-stamp").then(setImageBase64);
  }, []);


  console.log(imagedata)

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View>
          <Text style={styles.receiptCenter}>RECEIPT</Text>
        </View>
        <View style={styles.headerBox}>
          <View style={styles.logoSection}>
            <View style={styles.logoWrap}>
              {
                (profile.orignal_logoname === "" || profile.orignal_logoname === null) ?
                  <Image src={logoBase64} style={styles.logo1} /> :
                  <Text style={styles.headerTop}>{profile.orignal_logoname}</Text>
              }

            </View>
            <Text style={styles.headerP}>{address}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text>Receipt Date: {receipt.receiptDate}</Text>
            <Text>Reciept Id #: {receipt.invoiceNumber}</Text>
            <Text>Phone No.: +91 {phone}</Text>
            <Text>Mail ID: {email}</Text>
            {
              taxNo ? <Text >Tax ID: {taxNo}</Text> : null
            }

          </View>
        </View>

        {/* Billing Info */}
        <View style={styles.section}>
          <View>
            <Text style={styles.bold}>Billing To:</Text>
            <Text>{receipt.customerName}</Text>
          </View>
          <View>
            <Text style={styles.bold}>Due Date:</Text>
            <Text>{receipt.receiptDate}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.tableHeader}>
          <Text style={styles.cellWide}>Item</Text>
          <Text style={styles.cell}>Qty</Text>
          <Text style={styles.cell}>Rate</Text>
          <Text style={styles.cell}>Tax %</Text>
          <Text style={styles.cell}>Discount %</Text>
          <Text style={styles.cell}>Total</Text>
        </View>

        {items.map((item, idx) => {
          const qty = parseFloat(item.quantity) || 0;
          const rate = parseFloat(item.rate) || 0;
          const discount = parseFloat(item.discount) || 0;
          const tax = parseFloat(item.tax) || 0;

          const price = qty * rate;
          const discounted = price - (price * discount) / 100;
          const taxed = discounted + (discounted * tax) / 100;

          const title =
            typeof item?.item === "object" && item.item?.title
              ? item.item.title
              : typeof item?.title === "string"
                ? item.title
                : "Unnamed Item";

          return (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.cellWide}>
                {title}
              </Text>


              <Text style={styles.cell}>{qty}</Text>
              <Text style={styles.cell}>Rs. {rate}</Text>
              <Text style={styles.cell}>{tax}%</Text>
              <Text style={styles.cell}>{discount}%</Text>
              <Text style={styles.cell}>Rs. {taxed.toFixed(2)}</Text>
            </View>
          );
        })}


        {/* Summary Box */}
        <View style={styles.summaryBox}>
          <View >
          <Image
            src="https://ecommerce.100billionoms.com/image-stamp"
            style={{
              position: "absolute",
              // top: "30%",     
              left: "42%",    
              width: 100,
              height: 70,
              opacity: 1,
              // border:"1px solid red"
            }}
          />
          </View>
          <View style={styles.summaryRow}>
            <Text>Subtotal</Text>
            <Text>Rs. {parseFloat(invoice?.subtotal || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Discount</Text>
            <Text>Rs.{parseFloat(invoice?.discount_amount || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Tax</Text>
            <Text>Rs.{parseFloat(invoice?.tax || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Shipping</Text>
            <Text>Rs.{parseFloat(invoice?.shipping || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Advance Payment</Text>
            <Text>Rs.{parseFloat(invoice?.advance_payment || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Grand Total</Text>
            <Text>Rs.{parseFloat(invoice?.grand_total || 0).toFixed(2)}</Text>
          </View>

          {/* <View style={styles.summaryRow}>
            <Text>Amount to be Paid</Text>
            <Text>₹{parseFloat(invoice?.pending || 0).toFixed(2)}</Text>
          </View> */}
          {/* <View style={styles.summaryRow}>
            <Text>Pending Amount</Text>
            <Text>₹{parseFloat(invoice?.pending || 0).toFixed(2)}</Text>
          </View> */}
        </View>

        <View style={styles.noteSection}>
          <Text style={styles.noteLabel}>Receipt Details:</Text>

        </View>

        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text>Amount Paid</Text>
            <Text>Rs.{parseFloat(receipt.amount).toFixed(2)}</Text>
          </View>

        </View>

        {/* Grand Total */}
        <Text style={styles.grandTotal}>
          Receipt Generated for Rs.{parseFloat(receipt.amount).toFixed(2)}
        </Text>



        <View style={styles.noteSection}>
          <Text style={styles.noteLabel}>Pending Amount: Rs.{parseFloat(invoice?.pending || 0).toFixed(2)}</Text>

        </View>

        {/* Notes */}
        {invoice && (
          <View style={styles.noteSection}>
            {renderNoteAsPDF(invoice.customer_note || '<p>No notes</p>')}
          </View>
        )}


      </Page>
    </Document>
  );
};

export default ReceiptDocument;
